import {
  ChatAlt2Icon,
  DocumentTextIcon,
  GlobeAltIcon,
  InboxInIcon,
  MoonIcon,
} from '@heroicons/react/outline';
import type { ComponentType, ReactNode, SVGProps } from 'react';

import agents from '@/components/agents/agents.module.css';

import clsxm from '@/lib/clsxm';

import {
  CheckGlyph,
  delay,
  SectionHeading,
  tone,
} from '@/components/agents/primitives';
import { useReveal } from '@/components/agents/useReveal';

import {
  BRAND_BG,
  BRAND_SOFT,
  CalendarGlyph,
  QUIET,
  useReceptionistT,
} from './kit';

type Icon = ComponentType<SVGProps<SVGSVGElement>>;

/** The calls a front desk spends its day on, and how each one is handled. */
export default function ReceptionistJobs() {
  const { t } = useReceptionistT();
  const { ref, state } = useReveal<HTMLDivElement>(0.12);

  return (
    <section id='what-it-does' className='py-20 sm:py-28'>
      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <div className='grid gap-8 lg:grid-cols-12 lg:items-end'>
          <SectionHeading
            className='lg:col-span-7'
            title={t('Everything your front desk does on the phone.')}
            tail={t('Without the hold music.')}
          />
          <p className={clsxm('text-lg leading-8 lg:col-span-5', tone.body)}>
            {t(
              'Your receptionist learns your business from your website and documents, then takes the calls that keep pulling you away from work.',
            )}
          </p>
        </div>

        <div
          ref={ref}
          data-reveal={state}
          className={clsxm(agents.reveal, 'mt-14 grid gap-4 lg:grid-cols-12')}
        >
          <JobCard
            className='lg:col-span-7'
            at={0}
            icon={ChatAlt2Icon}
            title={t('Answers questions')}
            detail={t(
              'Opening hours, prices, services, the areas you cover: it answers from what you’ve taught it, in a natural voice.',
            )}
          >
            <QuestionsVisual />
          </JobCard>
          <JobCard
            className='lg:col-span-5'
            at={90}
            icon={CalendarGlyph}
            title={t('Books appointments')}
            detail={t(
              'With Google Calendar connected, it checks your free times while the caller is on the line and books the one they pick.',
            )}
          >
            <BookingVisual />
          </JobCard>
          <JobCard
            className='lg:col-span-5'
            at={180}
            icon={InboxInIcon}
            title={t('Takes messages and leads')}
            detail={t(
              'It asks for a name, a number and the reason for the call, then emails your team and saves the details to Leads.',
            )}
          >
            <MessageVisual />
          </JobCard>
          <JobCard
            className='lg:col-span-7'
            at={270}
            icon={MoonIcon}
            title={t('Answers after hours')}
            detail={t(
              'Nights, weekends and holidays. Callers get an answer instead of a voicemail beep, and you find the booking in your calendar in the morning.',
            )}
          >
            <AfterHoursVisual />
          </JobCard>
        </div>
      </div>
    </section>
  );
}

function JobCard({
  icon: Icon,
  title,
  detail,
  at,
  className,
  children,
}: {
  icon: Icon;
  title: string;
  detail: string;
  at: number;
  className?: string;
  children: ReactNode;
}) {
  return (
    <article
      className={clsxm(
        agents.rv,
        'flex min-w-0 flex-col rounded-[26px] p-6 sm:p-8',
        tone.surface,
        tone.line,
        className,
      )}
      style={delay(at)}
    >
      <span className='flex h-11 w-11 items-center justify-center rounded-2xl bg-primary-50 text-primary-600 ring-1 ring-inset ring-primary-600/10 dark:bg-primary-400/10 dark:text-primary-300 dark:ring-primary-400/20'>
        <Icon className='h-5 w-5' />
      </span>
      <h3
        className={clsxm(
          'mt-6 text-xl font-semibold tracking-[-0.01em]',
          tone.ink,
        )}
      >
        {title}
      </h3>
      <p className={clsxm('mt-2 max-w-xl text-[15px] leading-7', tone.body)}>
        {detail}
      </p>
      <div
        aria-hidden='true'
        className='mt-8 flex flex-1 flex-col justify-center rounded-[20px] bg-[#f4f4fb] p-4 sm:p-5 dark:bg-white/[0.03]'
      >
        {children}
      </div>
    </article>
  );
}

function CallerLine({ children }: { children: ReactNode }) {
  return (
    <div className='flex justify-end'>
      <p
        className={clsxm(
          'max-w-[85%] rounded-2xl rounded-ee-md bg-white px-3.5 py-2 shadow-[0_1px_2px_rgba(15,15,40,0.06)] dark:bg-white/[0.08]',
          tone.ink,
        )}
      >
        {children}
      </p>
    </div>
  );
}

function AgentLine({ children }: { children: ReactNode }) {
  return (
    <p
      className={clsxm(
        'max-w-[85%] rounded-2xl rounded-es-md px-3.5 py-2 text-white',
        BRAND_BG,
      )}
    >
      {children}
    </p>
  );
}

function QuestionsVisual() {
  const { t } = useReceptionistT();
  const sources: { icon: Icon; label: string }[] = [
    { icon: GlobeAltIcon, label: t('Your website') },
    { icon: DocumentTextIcon, label: t('Price list.pdf') },
    { icon: DocumentTextIcon, label: t('Service areas.docx') },
  ];

  return (
    <div>
      <div className='space-y-2.5 text-[13.5px] leading-relaxed'>
        <CallerLine>{t('Are you open on Saturdays?')}</CallerLine>
        <AgentLine>
          {t('Yes, from 8 a.m. to 2 p.m. Would you like me to book you in?')}
        </AgentLine>
        <CallerLine>{t('Do you install tankless water heaters?')}</CallerLine>
        <AgentLine>
          {t('We do, all the major brands. Most installs take a day.')}
        </AgentLine>
      </div>
      <div className='mt-5 flex flex-wrap items-center gap-2 border-t border-black/[0.06] pt-4 dark:border-white/[0.06]'>
        <span className={clsxm('me-1 text-xs font-medium', QUIET)}>
          {t('Learned from')}
        </span>
        {sources.map(({ icon: SourceIcon, label }) => (
          <span
            key={label}
            className={clsxm(
              'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium',
              tone.surface,
              tone.line,
              tone.body,
            )}
          >
            <SourceIcon className='h-3.5 w-3.5 flex-none' />
            {label}
          </span>
        ))}
      </div>
    </div>
  );
}

function BookingVisual() {
  const { t } = useReceptionistT();
  const slots = [t('8 a.m.'), t('10:30 a.m.'), t('1 p.m.'), t('3:30 p.m.')];
  const picked = 1;

  return (
    <div>
      <p className={clsxm('text-xs font-medium', QUIET)}>
        {t('Free on Thursday')}
      </p>
      <div className='mt-3 flex flex-wrap gap-2'>
        {slots.map((slot, index) => (
          <span
            key={slot}
            className={clsxm(
              'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[13px] font-semibold tabular-nums',
              index === picked
                ? clsxm(BRAND_BG, 'text-white')
                : clsxm(tone.surface, tone.line, tone.body),
            )}
          >
            {index === picked && <CheckGlyph className='h-3.5 w-3.5' />}
            {slot}
          </span>
        ))}
      </div>
      <div
        className={clsxm(
          'mt-4 flex items-center gap-3 rounded-2xl px-3.5 py-3',
          tone.surface,
          tone.line,
        )}
      >
        <span className='flex h-9 w-9 flex-none items-center justify-center rounded-xl bg-[#1a73e8]/10 text-[#1a73e8] dark:bg-[#1a73e8]/20 dark:text-[#8ab4f8]'>
          <CalendarGlyph className='h-[18px] w-[18px]' />
        </span>
        <span className='min-w-0 flex-1'>
          <span
            className={clsxm(
              'block text-[13px] font-semibold leading-tight',
              tone.ink,
            )}
          >
            {t('Booked in Google Calendar')}
          </span>
          <span className={clsxm('mt-0.5 block text-xs leading-snug', QUIET)}>
            {t('Thursday, 10:30 a.m.')} · {t('Leak inspection')}
          </span>
        </span>
        <CheckGlyph className='h-4 w-4 text-emerald-500' />
      </div>
    </div>
  );
}

function MessageVisual() {
  const { t } = useReceptionistT();
  const fields = [
    { label: t('Name'), value: 'Sam Patel', ltr: false },
    { label: t('Phone'), value: '+1 (512) 555-0163', ltr: true },
    { label: t('Reason'), value: t('A quote for a bathroom remodel') },
  ];

  return (
    <div
      className={clsxm(
        'rounded-2xl p-4 shadow-[0_18px_40px_-28px_rgba(30,20,90,0.5)]',
        tone.surface,
        tone.line,
      )}
    >
      <div className='flex items-center gap-2.5'>
        <span
          className={clsxm(
            'flex h-8 w-8 flex-none items-center justify-center rounded-full',
            BRAND_SOFT,
          )}
        >
          <InboxInIcon className='h-4 w-4' />
        </span>
        <p
          className={clsxm(
            'min-w-0 flex-1 truncate text-[13px] font-semibold',
            tone.ink,
          )}
        >
          {t('New message from a caller')}
        </p>
        <span className={clsxm('flex-none text-xs tabular-nums', QUIET)}>
          {t('2:14 p.m.')}
        </span>
      </div>
      <dl className='mt-3.5 grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1.5 text-[13px]'>
        {fields.map(({ label, value, ltr }) => (
          <div key={label} className='col-span-2 grid grid-cols-subgrid'>
            <dt className={QUIET}>{label}</dt>
            <dd className={clsxm('truncate font-medium', tone.ink)}>
              <span dir={ltr ? 'ltr' : undefined}>{value}</span>
            </dd>
          </div>
        ))}
      </dl>
      <div className='mt-4 flex flex-wrap gap-2'>
        {[t('Emailed to your team'), t('Saved to Leads')].map((label) => (
          <span
            key={label}
            className='inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 px-2.5 py-1 text-xs font-semibold text-emerald-700 dark:text-emerald-300'
          >
            <CheckGlyph className='h-3.5 w-3.5' />
            {label}
          </span>
        ))}
      </div>
    </div>
  );
}

// Calls across one day, in hours since midnight. The shop is open 8 to 18.
const CALLS = [2.25, 6.8, 9.4, 12.6, 15.1, 19.5, 21.8];
const OPEN = { from: 8, to: 18 };

function AfterHoursVisual() {
  const { t } = useReceptionistT();
  const ticks = [
    t('12 a.m.'),
    t('6 a.m.'),
    t('12 p.m.'),
    t('6 p.m.'),
    t('12 a.m.'),
  ];
  const at = (hour: number) => `${(hour / 24) * 100}%`;

  return (
    <div>
      <div className='flex flex-wrap items-center gap-x-5 gap-y-2 text-xs font-medium'>
        <span className={clsxm('flex items-center gap-2', QUIET)}>
          <span className='h-2.5 w-5 rounded-full bg-primary-200 dark:bg-primary-400/30' />
          {t('Open')}
        </span>
        <span className={clsxm('flex items-center gap-2', QUIET)}>
          <span className={clsxm('h-2.5 w-2.5 rounded-full', BRAND_BG)} />
          {t('Call answered')}
        </span>
      </div>

      <div className='relative mt-10 h-16'>
        <span className='absolute inset-x-0 top-[22px] h-2 rounded-full bg-black/[0.06] dark:bg-white/[0.08]' />
        <span
          className='absolute top-[22px] h-2 rounded-full bg-primary-200 dark:bg-primary-400/30'
          style={{
            insetInlineStart: at(OPEN.from),
            width: at(OPEN.to - OPEN.from),
          }}
        />
        {CALLS.map((hour) => {
          const late = hour < OPEN.from || hour >= OPEN.to;
          return (
            <span
              key={hour}
              className={clsxm(
                'absolute top-[18px] flex h-4 w-4 -translate-x-1/2 items-center justify-center rounded-full ring-[3px] ring-[#f4f4fb] rtl:translate-x-1/2 dark:ring-[#17161f]',
                late ? BRAND_BG : 'bg-[#9a9ab3] dark:bg-[#5d5d78]',
              )}
              style={{ insetInlineStart: at(hour) }}
            >
              {late && <CheckGlyph className='h-2.5 w-2.5 text-white' />}
            </span>
          );
        })}
        {/* Ends just past the last dot, so it never runs off the edge. */}
        <span
          className={clsxm(
            'absolute bottom-[calc(100%-4px)] whitespace-nowrap rounded-lg px-2 py-1 text-[11px] font-semibold',
            BRAND_SOFT,
          )}
          style={{ insetInlineEnd: `calc(${100 - (21.8 / 24) * 100}% - 14px)` }}
        >
          {t('9:47 p.m.')} · {t('Booked')}
        </span>
        <div
          className={clsxm(
            'absolute inset-x-0 bottom-0 h-4 text-[11px] tabular-nums',
            QUIET,
          )}
        >
          {ticks.map((tick, index) => {
            const last = index === ticks.length - 1;
            return (
              <span
                key={index}
                className={clsxm(
                  'absolute top-0 whitespace-nowrap',
                  index % 2 === 1 && 'hidden sm:block',
                  index > 0 && !last && '-translate-x-1/2 rtl:translate-x-1/2',
                  last && '-translate-x-full rtl:translate-x-full',
                )}
                style={{
                  insetInlineStart: `${(index / (ticks.length - 1)) * 100}%`,
                }}
              >
                {tick}
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
}
